import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import {
  type DatabaseProjectionQuery,
  type DatabaseProjectionSource,
  type DataSourceCellsWindow,
  type DataSourceCellsWindowRequest,
  type DataSourceRowsWindow,
  type PropertyMetaConfig,
} from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { type BlockModel } from '@blocksuite/store';

import { type DataSourceWriteActor } from './data-source-contracts.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import {
  isReadonlyYMap,
  normalizeDatabasePropertyWindowRequest,
  SCHEMA_LOCKED_INVALID_CELL_VALUE,
} from './data-source-values.js';
import { getCell, updateCell } from './utils/block-utils.js';

export function _runCapture(this: DatabaseSourceRuntime): void {
  if (this._disposed) {
    return;
  }
  if (this._batch) {
    return;
  }

  this._batch = requestAnimationFrame(() => {
    this.captureStorageSync();
    this._batch = 0;
  });
}

export function getModelById(
  this: DatabaseSourceRuntime,
  rowId: string
): BlockModel | undefined {
  return this._model.children[this._model.childMap.value.get(rowId) ?? -1];
}

export function getRowsWindow(
  this: DatabaseSourceRuntime,
  request: {
    offset?: number;
    limit: number;
  }
): DataSourceRowsWindow {
  if (this.isRowSharded) {
    return this.getShardedRowsWindow(request);
  }
  return this.getUnshardedRowsWindow(request);
}

export function getProjectionSource(
  this: DatabaseSourceRuntime,
  query: DatabaseProjectionQuery
): DatabaseProjectionSource {
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) {
    return this.getUnshardedProjectionSource(query);
  }

  // Sharded projections read the materialized row cache directly instead of
  // sourceRowIds$. Track its revision explicitly so computed consumers (for
  // example grouped Kanban lanes) see rows and group keys added by local or
  // remote shard updates without requiring a view reload.
  this._shardedRowsRevision.value;
  this.materializeAllShardedRows();
  const integrityError = this._rowShardIntegrityError.value;
  if (integrityError) {
    return {
      complete: false,
      diagnostics: [
        {
          code: 'partial-source',
          message: integrityError,
        },
      ],
      freshness: 'stale',
      rowIds: [],
      source: 'local-window',
      syncState: 'partial',
    };
  }
  const complete = this.areAllManifestShardRowsMaterialized(manifest);
  return {
    complete,
    diagnostics: [],
    freshness: complete ? 'live' : 'stale',
    rowIds: this._shardedRowIds.slice(),
    source: complete ? 'local-shards' : 'local-window',
    syncState: complete ? 'live' : 'partial',
  };
}

export function getSearchCandidateRowIds(
  this: DatabaseSourceRuntime,
  rowIds: readonly string[],
  searchText: string,
  propertyIds: readonly string[]
): readonly string[] {
  if (!this.isRowSharded) {
    return rowIds;
  }
  const needle = searchText.trim().toLowerCase();
  if (!needle) return rowIds;
  return rowIds.filter(rowId =>
    this.getShardedRowSearchText(rowId, propertyIds).includes(needle)
  );
}

export function getCellsWindow(
  this: DatabaseSourceRuntime,
  request: DataSourceCellsWindowRequest
): DataSourceCellsWindow {
  if (!this.isRowSharded) {
    return this.getUnshardedCellsWindow(request);
  }

  const rowsWindow = this.getShardedRowsWindow(request);
  const propertySource =
    request.propertyIds ?? this.getBoundedPropertyIdsWindow().propertyIds;
  const { propertyOffset, propertyLimit, propertyIds } =
    normalizeDatabasePropertyWindowRequest(request, propertySource);
  const cells: Record<string, Record<string, unknown>> = {};

  for (const rowId of rowsWindow.rowIds) {
    const rowCells: Record<string, unknown> = {};
    for (const propertyId of propertyIds) {
      rowCells[propertyId] = this.cellValueGetFromLoadedRows(rowId, propertyId);
    }
    cells[rowId] = rowCells;
  }

  return {
    offset: rowsWindow.offset,
    limit: rowsWindow.limit,
    propertyOffset,
    propertyLimit,
    totalRowCount: rowsWindow.totalCount,
    totalColumnCount: this.columnCount$.value,
    rowIds: rowsWindow.rowIds,
    propertyIds,
    cells,
  };
}

export function getLogicalRowIdsSnapshot(
  this: DatabaseSourceRuntime
): string[] {
  if (this.isRowSharded) {
    this.materializeAllShardedRows();
    return this._shardedRowIds.slice();
  }
  return this.sourceRowIds$.value.slice();
}

export function rowExists(this: DatabaseSourceRuntime, rowId: string): boolean {
  if (this.isRowSharded) {
    this.materializeAllShardedRows();
    return this._shardedRowsById.has(rowId);
  }
  return this._model.childMap.value.has(rowId);
}

export function newPropertyName(
  this: DatabaseSourceRuntime,
  prefix = 'Column'
): string {
  let i = 1;
  const hasSameName = (name: string) => {
    return this._model.props.columns$.value.some(
      column => column.name === name
    );
  };
  while (true) {
    let name = i === 1 ? prefix : `${prefix} ${i}`;
    if (!hasSameName(name)) {
      return name;
    }
    i++;
  }
}

export function cellValueChange(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown,
  actor: DataSourceWriteActor = 'user'
): void {
  this.assertFileIndexCellMutationAllowed(propertyId, actor);
  // sl-d7zo.7.4: Row-sharded databases write cell values into the owning
  // row-shard Yjs doc.
  if (this.isRowSharded) {
    this.cellValueChangeSharded(rowId, propertyId, value);
    return;
  }

  this.assertNoLocalRootRowMutation('Cell update');
  this._runCapture();

  const type = this.propertyTypeGet(propertyId);
  if (type == null) {
    return;
  }
  const propertyData = this.propertyDataGet(propertyId);
  const newValue = this.normalizeSchemaLockedSelectValue(
    type,
    propertyData,
    value
  );
  if (newValue === SCHEMA_LOCKED_INVALID_CELL_VALUE) {
    return;
  }
  const update = this.propertyMetaGet(type)?.config.rawValue.setValue;
  const old = this.cellValueGet(rowId, propertyId);
  const updateFn =
    update ??
    (({ setValue, newValue }) => {
      setValue(newValue);
    });
  updateFn({
    value: old,
    data: propertyData,
    dataSource: this.dataSource,
    newValue,
    setValue: newValue => {
      if (this._model.props.columns$.value.some(v => v.id === propertyId)) {
        updateCell(this._model, rowId, {
          columnId: propertyId,
          value: newValue,
        });
      }
    },
  });
}

export function cellValuesChange(
  this: DatabaseSourceRuntime,
  rowId: string,
  values: Readonly<Record<string, unknown>>,
  actor: DataSourceWriteActor = 'user'
): void {
  const entries = Object.entries(values);
  if (entries.length === 0) return;
  if (!this.isRowSharded) {
    for (const [propertyId, value] of entries) {
      this.cellValueChange(rowId, propertyId, value, actor);
    }
    return;
  }

  const cells = entries.flatMap(([propertyId, value]) => {
    const cell = this.resolveShardedCellForWrite(
      rowId,
      propertyId,
      value,
      actor
    );
    return cell ? [[propertyId, cell] as const] : [];
  });
  if (cells.length === 0) return;

  const { shard, shardIndex, shardDoc } = this.findWritableShardForRow(rowId);
  const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const rowMap = rowsMap.get(rowId);
  if (!isReadonlyYMap(rowMap)) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Row "${rowId}" is missing from sharded database "${this.databaseId}".`
    );
  }
  shardDoc.spaceDoc.transact(() => {
    for (const [propertyId, cell] of cells) {
      rowMap.set(propertyId, cell);
    }
    this.touchShardedRowSystemFields(rowId, rowMap);
  });
  const rowIds = shardDoc.spaceDoc
    .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
    .toArray()
    .map(id => String(id));
  this.updateLocalShardEntryAtIndex(shardIndex, shard, rowIds);
  this.refreshShardedRowsFromDocs();
}

export function cellValueChangeSharded(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown
): void {
  const type = this.propertyTypeGet(propertyId);
  if (type == null) {
    return;
  }
  const propertyData = this.propertyDataGet(propertyId);
  const newValue = this.normalizeSchemaLockedSelectValue(
    type,
    propertyData,
    value
  );
  if (newValue === SCHEMA_LOCKED_INVALID_CELL_VALUE) {
    return;
  }

  const resolvedValue = this.resolveRawValueForProperty(
    rowId,
    propertyId,
    type,
    propertyData,
    newValue
  );
  const persistedValue = this.serializeRawValueForProperty(
    type,
    propertyData,
    resolvedValue
  );

  this.writeShardedCellToLocalDoc(rowId, propertyId, persistedValue);
}

export function resolveRawValueForProperty(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  type: string,
  propertyData: Record<string, unknown>,
  newValue: unknown
): unknown {
  const meta = this.propertyMetaGet(type);
  const rawSetter = meta?.config.rawValue.setValue;
  let resolvedValue: unknown = newValue;
  const updateFn =
    rawSetter ??
    (({ setValue, newValue }) => {
      setValue(newValue);
    });
  updateFn({
    value: this.cellValueGet(rowId, propertyId),
    data: propertyData,
    dataSource: this.dataSource,
    newValue,
    setValue: nextValue => {
      resolvedValue = nextValue;
    },
  });
  return resolvedValue;
}

export function serializeRawValueForProperty(
  this: DatabaseSourceRuntime,
  type: string,
  propertyData: Record<string, unknown>,
  value: unknown
): unknown {
  const rawValue = this.propertyMetaGet(type)?.config.rawValue;
  const rawToJson = rawValue?.toJson;
  const rawFromJson = rawValue?.fromJson;
  // `toJson` is the semantic view value used by filters/renderers; for types
  // without a matching `fromJson` it is not a persistence codec. Attachment
  // cells, for example, expose file names as JSON but must keep the file
  // record in row-shard storage.
  if (!rawFromJson) {
    return value;
  }
  return rawToJson
    ? rawToJson({
        value,
        data: propertyData,
        dataSource: this.dataSource,
      })
    : value;
}

export function normalizeSchemaLockedSelectValue(
  this: DatabaseSourceRuntime,
  type: string,
  data: Record<string, unknown>,
  value: unknown
): unknown {
  if (this._model.props.schemaLocked !== true) return value;
  if (type !== 'select' && type !== 'multi-select') return value;
  const options = Array.isArray(data.options)
    ? (data.options as Array<{ id?: unknown; value?: unknown }>)
    : [];
  const resolve = (
    raw: unknown
  ): string | typeof SCHEMA_LOCKED_INVALID_CELL_VALUE => {
    const text = String(raw);
    const option = options.find(
      option => option.id === text || option.value === text
    );
    return typeof option?.id === 'string'
      ? option.id
      : SCHEMA_LOCKED_INVALID_CELL_VALUE;
  };
  if (type === 'select') {
    if (value === null || value === undefined || value === '') return value;
    return resolve(value);
  }
  const values = Array.isArray(value)
    ? value
    : value === null || value === undefined || value === ''
      ? []
      : [value];
  const resolved = values.map(resolve);
  return resolved.includes(SCHEMA_LOCKED_INVALID_CELL_VALUE)
    ? SCHEMA_LOCKED_INVALID_CELL_VALUE
    : resolved;
}

export function cellValueGet(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  if (this.isRowSharded) {
    if (!this._shardedRowsById.has(rowId)) {
      this.loadShardedRowFromExistingDocs(rowId);
    }
    return this.cellValueGetFromLoadedRows(rowId, propertyId);
  }
  return this.cellValueGetFromLocalRows(rowId, propertyId);
}

export function cellValueGetFromLoadedRows(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  if (this.isRowSharded) {
    const shardedCell = this.getShardedCellValue(rowId, propertyId);
    if (shardedCell.found) {
      return shardedCell.value;
    }
    if (!this._shardedRowsById.has(rowId)) {
      return undefined;
    }
    return this.cellDefaultValueGet(rowId, propertyId);
  }
  return this.cellValueGetFromLocalRows(rowId, propertyId);
}

export function cellDefaultValueGet(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  if (this.isSpacialProperty(propertyId)) {
    return this.spacialValueGet(rowId, propertyId, propertyId);
  }
  const type = this.propertyTypeGet(propertyId);
  if (!type) {
    return;
  }
  if (this.isSpacialProperty(type)) {
    return this.spacialValueGet(rowId, propertyId, type);
  }
  const meta = this.propertyMetaGet(type);
  if (!meta) {
    return;
  }
  return this.normalizeRawCellValue(meta, meta.config.rawValue.default());
}

export function cellValueGetFromLocalRows(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  if (this.isSpacialProperty(propertyId)) {
    return this.spacialValueGet(rowId, propertyId, propertyId);
  }
  const type = this.propertyTypeGet(propertyId);
  if (!type) {
    return;
  }
  if (this.isSpacialProperty(type)) {
    return this.spacialValueGet(rowId, propertyId, type);
  }
  const meta = this.propertyMetaGet(type);
  if (!meta) {
    return;
  }
  const rawValue =
    getCell(this._model, rowId, propertyId)?.value ??
    meta.config.rawValue.default();
  return this.normalizeRawCellValue(meta, rawValue);
}

export function normalizeRawCellValue(
  this: DatabaseSourceRuntime,
  meta: PropertyMetaConfig,
  rawValue: unknown
): unknown {
  const result = meta.config.rawValue.schema.safeParse(rawValue);
  if (result.success) {
    return result.data;
  }
  return;
}
